
import { defineComponent, ref, onMounted } from 'vue'
import TheMainHeader from '@/common/components/TheMainHeader.vue'
import {
    RadioGroup,
    RadioGroupLabel,
    RadioGroupDescription,
    RadioGroupOption,
} from '@headlessui/vue'
import { useMutation, useQuery, useResult } from '@vue/apollo-composable'
import {
    GET_USER_QUERY,
    UPDATE_ADULT_MUTATION,
} from '@/services/apollo/requests'
import { GraphQLUserData } from '@/services/apollo/types'

enum AdultContentSetting {
    ADULT_ALLOWED = 'ADULT_ALLOWED',
    CONTENT_RESTRICTED = 'CONTENT_RESTRICTED',
    MATURE_ALLOWED = 'MATURE_ALLOWED',
}

const adultContentOptions = [
    {
        name: 'Restricted',
        description:
            'Sources will not have any sort of adult content available. Each title within is assumed safe for all audiences.',
        value: AdultContentSetting.CONTENT_RESTRICTED,
    },
    {
        name: 'Mature',
        description:
            'Shows content and sources which may not be guaranteed to contain content safe for all audiences. This is what we recommend most users use.',
        value: AdultContentSetting.MATURE_ALLOWED,
    },
    {
        name: 'Adult',
        description:
            'No content filtering. Additionally, this unlocks a setting menu inside of the Paperback application allowing you to change this setting on your device.',
        value: AdultContentSetting.ADULT_ALLOWED,
    },
]

export default defineComponent({
    name: 'Settings',
    components: {
        TheMainHeader,
        RadioGroup,
        RadioGroupLabel,
        RadioGroupDescription,
        RadioGroupOption,
    },

    setup() {
        const { result, onResult } = useQuery(GET_USER_QUERY)
        const { mutate: updateAdult } = useMutation(
            UPDATE_ADULT_MUTATION,
            () => ({
                variables: { status: adultContent.value },
                update: (cache) => {
                    const data = JSON.parse(
                        JSON.stringify(
                            cache.readQuery({
                                query: GET_USER_QUERY,
                            })
                        )
                    )
                    data.getUserData.adultEnabled = adultContent.value
                    cache.writeQuery({ query: GET_USER_QUERY, data })
                },
            })
        )

        const adultContentGQL = useResult(
            result,
            null,
            (data: GraphQLUserData) => data.getUserData?.adultEnabled
        )

        const adultContent = ref('')

        onResult(
            () =>
                (adultContent.value = (adultContentGQL.value as unknown) as string)
        )

        onMounted(() => {
            adultContent.value = (adultContentGQL.value as unknown) as string
        })

        return {
            adultContent,
            adultContentOptions,
            updateAdult,
        }
    },
})
